import { useCreateSearchModeMessage, type AssistantSearchMessage } from '@/entities/discovery';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import { cn } from '@/shared/utils';
import { RefreshCw } from 'lucide-react';
import { AssistantMessageRow } from './primitives';
import { useDiscoveryMessagesActions } from '../../stores/discovery-message-store';
import { buildAssistantLoadingMessage } from './build-message.helpers';
import { parseFilterConditions } from '../../helpers/parse-filter-conditions';

export const AssistantMessageSearch = ({
  message,
  isCurrent = false,
}: {
  message: AssistantSearchMessage;
  isCurrent?: boolean;
}) => {
  const { appendMessages, replaceMessages } = useDiscoveryMessagesActions();
  const executeSearch = useCreateSearchModeMessage();
  const { filter_conditions: filterConditions, post_feature_conditions: postFeatureConditions } =
    message.content.search_conditions;
  const parsedFilters = parseFilterConditions(
    filterConditions as Parameters<typeof parseFilterConditions>[0],
  );
  const hasFilters = parsedFilters.length > 0;
  const hasPostFeatures = postFeatureConditions ? postFeatureConditions.length > 0 : false;
  const handleReanalyze = async () => {
    appendMessages([buildAssistantLoadingMessage(message.discovery_session_id)]);
    const res = await executeSearch.mutateAsync({
      discoverySessionId: message.discovery_session_id,
      request: message.content,
    });
    replaceMessages([res]);
  };

  return (
    <AssistantMessageRow>
      <div className="mt-1 flex flex-1 flex-col gap-4">
        <span className="text-primary text-sm">{message.content.message}</span>
        {hasFilters || hasPostFeatures ? (
          <div
            className={cn(
              'bg-accent rounded-lg border px-4 py-3 transition-colors',
              isCurrent && 'border-primary border-2',
            )}
          >
            {isCurrent ? (
              <div className="mb-3 flex justify-start">
                <Badge variant="outline">Current results</Badge>
              </div>
            ) : null}
            {hasFilters ? (
              <>
                <h3 className="text-sm font-semibold">Filter analysis</h3>
                <ul className="my-2 ml-5 list-disc space-y-1 text-sm last:mb-0">
                  {parsedFilters.map((filter) => (
                    <li key={filter.filterName}>
                      {filter.filterName}: {filter.value.join(', ')}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            {hasPostFeatures ? (
              <>
                <h3 className="text-sm font-semibold">Video analysis</h3>
                <ul className="my-2 ml-5 list-disc space-y-1 text-sm last:mb-0">
                  {postFeatureConditions?.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </>
            ) : null}
            {!isCurrent ? (
              <div className="mt-3 flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={executeSearch.isPending}
                  loading={executeSearch.isPending}
                  onClick={handleReanalyze}
                >
                  <RefreshCw className="size-3.5" />
                  Search again
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </AssistantMessageRow>
  );
};
